import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'antd';
import { CloneIcon } from '@components/common/Icons/CloneIcon';
import { FormCalendar } from '@components/common/FormCalendar/FormCalendar';
import { FormSelect } from '@components/common/FormSelect/FormSelect';
import { InputAdornment } from '@components/common/InputAdornment';
import { FormTextField } from '@components/common/FormTextField/FormTextField';
import { LoadingButton } from '@components/common/LoadingButton/LoadingButton';
import { FormControlLabel } from '@components/common/FormControlLabel';
import { FormCheckbox } from '@components/common/FormCheckbox/FormCheckbox';
import { ISelectOption } from '@components/common/Select/types';
import { useGetUser } from '@queries/users/useGetUser';
import { IUpdateProductForm, UpdateProductDefaultValues } from './types';
import { getPageURLWithSection, PageURL } from '@router/pageURL';
import { useFormSelectOptions } from '../useFormSelectOptions';
import { useProductById } from '@queries/products/useProductById';
import { FormNumberField } from '@components/common/FormNumberField/FormNumberField';
import { productFormValuesFormatter } from '../AddProductPage/helpers';
import { IUpdateProductParams } from '@api/product/types';
import { BtlPropertyTypeEnum } from '@legacy/shared-types/enums/btl-property-type.enum';
import { IUpdateProductDTO } from '@legacy/shared-types/dto/product.type';
import { ProductCategoryEnum } from '@legacy/shared-types/enums/product-category.enum';
import { ProductTypeEnum } from '@legacy/shared-types/enums/product-type.enum';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { productApi } from '@api/product/productsApi';
import { PermissionType } from '@enums/permissionType.enum';
import { parseBooleanish } from '@utils/general';
export const EditProductPage = () => {
  const { data: user } = useGetUser();

  const userId = user?.id;

  const userGroups = user?.roles.map(r => r.role.type) ?? [];

  const intl = useIntl();
  const queryParams = useParams();
  const navigate = useNavigate();
  const {
    productTypeOptions,
    productPoolOptions,
    productRangeOptions,
    productCategoryOptions,
    btlExpatOptions,
    propertyCategoryOptions,
  } = useFormSelectOptions();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { mutate: updateProduct, isLoading } = useMutation(productApi.updateProduct);
  const { mutate: verifyProduct, isLoading: isVerifyLoading } = useMutation(productApi.verifyProduct);
  const { data: productDetails } = useProductById(queryParams.id);

  const today = dayjs();
  const isProductActive = productDetails?.isActive;
  const productCloseDate = productDetails?.closeDate ? dayjs(productDetails.closeDate) : null;
  const productLaunchDate = productDetails?.launchDate ? dayjs(productDetails.launchDate) : null;
  const isLaunchDateInPast = productLaunchDate ? productLaunchDate.isBefore(today, 'day') : false;
  const isProductVerified = Boolean(productDetails?.verifiedAt);

  const isUserProductCreator = productDetails?.creatorId === userId;
  const isProductCloseDateOutdated = productCloseDate ? productCloseDate.isBefore(today) : false;
  const isProductClosed = isProductCloseDateOutdated && !isProductActive && !isProductVerified;
  const isUserAdmin = userGroups.includes(PermissionType.ADMIN);
  const isUserHasAccessToProduct = isUserProductCreator ? true : isUserAdmin;
  const isVerifiedCheckBlocked =
    isUserProductCreator ||
    isProductActive ||
    isProductClosed ||
    isProductVerified ||
    !productLaunchDate ||
    (isLaunchDateInPast && !isProductVerified);

  const form = useForm<IUpdateProductForm>({
    defaultValues: UpdateProductDefaultValues,
  });

  const formFields = form.watch();
  const isDirtyFields = Boolean(Object.keys(form.formState.dirtyFields).length);

  useEffect(() => {
    if (productDetails)
      form.reset({
        ...productDetails,
        initialBaseRate: Number(productDetails.initialBaseRate * 100).toFixed(2),
        initialMargin: Number(productDetails.initialMargin * 100).toFixed(2),
        margin: Number(productDetails.margin * 100).toFixed(2),
        maximumFTV: Number(productDetails.maximumFTV * 100).toFixed(2),
        equityShare: Number(productDetails.equityShare * 100).toFixed(2),
        initialRate: Number(((productDetails.initialBaseRate + productDetails.initialMargin) * 100).toFixed(5)),
        launchDate: productDetails.launchDate ? dayjs(productDetails.launchDate) : null,
        closeDate: productDetails?.closeDate ? dayjs(productDetails.closeDate) : null,
        isVerified: Boolean(productDetails.verifiedAt),
        isExpatriate: parseBooleanish(productDetails.metadata?.btlIsExpat),
        propertyCategory: (productDetails.metadata?.btlPropertyType as BtlPropertyTypeEnum | undefined) ?? null,
        maxFti: productDetails.maxFti,
      });
  }, [productDetails, form]);

  useEffect(() => {
    if (formFields.productCategory !== ProductCategoryEnum.BUY_TO_LET) {
      form.setValue('isExpatriate', null);
      form.setValue('propertyCategory', null);
    }
  }, [form, formFields.productCategory]);

  const handleApiSuccess = () => {
    navigate(PageURL.PRODUCTS);
    if (queryParams.id) {
      queryClient.invalidateQueries(['products-list', queryParams.id]);
    }
  };

  const handleApiError = (error: any) => {
    const messages = error.message.split(';');
    messages.forEach((message: string) => enqueueSnackbar(message, { variant: 'error' }));
  };

  const submitFormHandler = (formData: IUpdateProductForm) => {
    const dirtyFields = Object.keys(form.formState.dirtyFields).reduce((acc: Record<string, any>, key) => {
      acc[key] = formFields[key as keyof IUpdateProductForm];

      return acc;
    }, {});

    const formattedData = Object.entries(dirtyFields).reduce((acc: Record<string, any>, [key, value]) => {
      if (key === 'isExpatriate' || key === 'propertyCategory' || key === 'maxFti') {
        return acc;
      }
      acc[key as keyof IUpdateProductDTO] = productFormValuesFormatter(key, value);

      return acc;
    }, {}) as IUpdateProductDTO;

    const dataToTransfer: IUpdateProductParams = { id: formData.id, data: formattedData };

    if (dirtyFields.isVerified) {
      verifyProduct(formData.id, { onSuccess: handleApiSuccess, onError: handleApiError });
    } else {
      updateProduct(dataToTransfer, { onSuccess: handleApiSuccess, onError: handleApiError });
    }
  };

  if (!productDetails) return <h1>Loading...</h1>;

  return (
    // Shrink-to-fit so the heading lines up with the left edge of the field grid.
    <div className={'mx-auto w-fit'}>
      <h1>Edit product</h1>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(submitFormHandler)} className={'flex flex-col items-center'}>
          <div className={'mb-8 grid grid-cols-4 gap-8'}>
            <div className={'mt-4'}>
              <FormTextField
                textFieldProps={{
                  disabled: true,
                  label: intl.formatMessage({ id: 'product.salesforceId.label' }),
                }}
                name={'salesforceId'}
              />
            </div>
            <div className={'mt-4'}>
              <FormTextField
                textFieldProps={{
                  disabled: isProductActive || isProductClosed || !isUserProductCreator,
                  label: intl.formatMessage({ id: 'product.description.label' }),
                }}
                name={'description'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                  maxLength: {
                    value: 80,
                    message: intl.formatMessage({ id: 'product.description.limit' }, { limit: 80 }),
                  },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormSelect
                name={'productCategory'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                }}
                options={productCategoryOptions}
                selectProps={{
                  disabled: true,
                  defaultValue: productCategoryOptions.find(option => option.value === productDetails.productCategory),
                  isOptionEqualToValue: (option: ISelectOption, selectedOption: ISelectOption) =>
                    option.value === selectedOption.value,
                }}
                selectInputProps={{
                  placeholder: intl.formatMessage({ id: 'placeholders.select' }),
                  label: intl.formatMessage({ id: 'product.productCategory.label' }),
                  InputLabelProps: { shrink: true },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormSelect
                name={'productRange'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                }}
                options={productRangeOptions}
                selectProps={{
                  disabled: isProductActive || isProductClosed || !isUserProductCreator,
                  defaultValue: productRangeOptions.find(option => option.value === productDetails.productRange),
                  isOptionEqualToValue: (option: ISelectOption, selectedOption: ISelectOption) =>
                    option.value === selectedOption.value,
                }}
                selectInputProps={{
                  placeholder: intl.formatMessage({ id: 'placeholders.select' }),
                  label: intl.formatMessage({ id: 'product.productRange.label' }),
                  InputLabelProps: { shrink: true },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormSelect
                name={'productType'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                }}
                options={productTypeOptions}
                selectProps={{
                  disabled: isProductActive || isProductClosed || !isUserProductCreator,
                  isOptionEqualToValue: (option: ISelectOption, selectedOption: ISelectOption) =>
                    option.value === selectedOption.value,
                  defaultValue: productTypeOptions.find(option => option.value === productDetails.productType),
                }}
                selectInputProps={{
                  label: intl.formatMessage({
                    id: 'product.productType.label',
                  }),
                  InputLabelProps: { shrink: true },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  disabled: isProductActive || isProductClosed || !isUserProductCreator,
                  label: intl.formatMessage({ id: 'product.monthsInitial.label' }),
                }}
                name={'monthsInitial'}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  disabled: isProductActive || isProductClosed || !isUserProductCreator,
                  label: intl.formatMessage({ id: 'product.maximumFTV.label' }),
                  InputProps: {
                    endAdornment: <InputAdornment position='end'>%</InputAdornment>,
                    sx: { '& .MuiInputAdornment-root > p': { opacity: '0.7' } },
                  },
                }}
                name={'maximumFTV'}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  disabled: true,
                  label: intl.formatMessage({ id: 'product.maxFti.label' }),
                  inputProps: { step: 0.1 },
                }}
                name={'maxFti'}
              />
            </div>

            {formFields.productCategory === ProductCategoryEnum.BUY_TO_LET ? (
              <>
                <div className={'mt-4'}>
                  <FormSelect
                    name={'isExpatriate'}
                    rules={{
                      validate: (value: boolean | null) => {
                        if (formFields.productCategory !== ProductCategoryEnum.BUY_TO_LET) {
                          return true;
                        }
                        if (value === null || value === undefined) {
                          return intl.formatMessage({ id: 'common.error.requiredField' });
                        }
                        return true;
                      },
                    }}
                    options={btlExpatOptions}
                    selectProps={{
                      disabled: true,
                      isOptionEqualToValue: (option: ISelectOption, selectedOption: ISelectOption) =>
                        option.value === selectedOption.value,
                    }}
                    selectInputProps={{
                      placeholder: intl.formatMessage({ id: 'placeholders.select' }),
                      label: intl.formatMessage({ id: 'product.isExpatriate.label' }),
                      InputLabelProps: { shrink: true },
                    }}
                  />
                </div>
                <div className={'mt-4'}>
                  <FormSelect
                    name={'propertyCategory'}
                    rules={{
                      validate: (value: string | null) => {
                        if (formFields.productCategory !== ProductCategoryEnum.BUY_TO_LET) {
                          return true;
                        }
                        if (value === null || value === undefined || value === '') {
                          return intl.formatMessage({ id: 'common.error.requiredField' });
                        }
                        return true;
                      },
                    }}
                    options={propertyCategoryOptions}
                    selectProps={{
                      disabled: true,
                      isOptionEqualToValue: (option: ISelectOption, selectedOption: ISelectOption) =>
                        option.value === selectedOption.value,
                    }}
                    selectInputProps={{
                      placeholder: intl.formatMessage({ id: 'placeholders.select' }),
                      label: intl.formatMessage({ id: 'product.propertyCategory.label' }),
                      InputLabelProps: { shrink: true },
                    }}
                  />
                </div>
              </>
            ) : null}

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  disabled:
                    isProductActive ||
                    isProductClosed ||
                    !isUserProductCreator ||
                    formFields.productType === 'DISCOUNT',
                  label: intl.formatMessage({ id: 'product.initialBaseRate.label' }),
                  variant: 'outlined',
                  InputProps: {
                    endAdornment: <InputAdornment position='end'>%</InputAdornment>,
                    sx: { '& .MuiInputAdornment-root > p': { opacity: '0.7' } },
                  },
                }}
                name={'initialBaseRate'}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  disabled:
                    isProductActive ||
                    isProductClosed ||
                    !isUserProductCreator ||
                    formFields.productType === ProductTypeEnum.FIXED,
                  label: intl.formatMessage({ id: 'product.initialMargin.label' }),
                  InputProps: {
                    endAdornment: <InputAdornment position='end'>%</InputAdornment>,
                    sx: { '& .MuiInputAdornment-root > p': { opacity: '0.7' } },
                  },
                }}
                name={'initialMargin'}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  disabled: true,
                  label: intl.formatMessage({ id: 'product.initialRate.label' }),
                  InputProps: {
                    endAdornment: <InputAdornment position='end'>%</InputAdornment>,
                    sx: { '& .MuiInputAdornment-root > p': { opacity: '0.7' } },
                  },
                }}
                name={'initialRate'}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  disabled: isProductActive || isProductClosed || !isUserProductCreator,
                  label: intl.formatMessage({ id: 'product.margin.label' }),
                  InputProps: {
                    endAdornment: <InputAdornment position='end'>%</InputAdornment>,
                    sx: { '& .MuiInputAdornment-root > p': { opacity: '0.7' } },
                  },
                }}
                name={'margin'}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  disabled: isProductActive || isProductClosed || !isUserProductCreator,
                  label: intl.formatMessage({ id: 'product.productFees.label' }),
                }}
                name={'productFees'}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  disabled: isProductActive || isProductClosed || !isUserProductCreator,
                  label: intl.formatMessage({ id: 'product.productFeesPercentage.label' }),
                }}
                name={'productFeesPercentage'}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  disabled: isProductActive || isProductClosed || !isUserProductCreator,
                  label: intl.formatMessage({ id: 'product.equityShare.label' }),
                  InputProps: {
                    endAdornment: <InputAdornment position='end'>%</InputAdornment>,
                    sx: { '& .MuiInputAdornment-root > p': { opacity: '0.7' } },
                  },
                }}
                name={'equityShare'}
              />
            </div>

            <div className={'mt-4'}>
              <FormSelect
                name={'productTier'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                }}
                options={productPoolOptions}
                selectProps={{
                  disabled: isProductActive || isProductClosed || !isUserProductCreator,
                  isOptionEqualToValue: (option: ISelectOption, selectedOption: ISelectOption) =>
                    option.value === selectedOption.value,
                  defaultValue: productPoolOptions.find(option => option.value === productDetails.productTier),
                }}
                selectInputProps={{
                  label: intl.formatMessage({ id: 'product.productTier.label' }),
                  InputLabelProps: { shrink: true },
                }}
              />
            </div>
            <div className={'mt-4'}>
              <FormCalendar
                disabled={isProductActive || isProductClosed || !isUserProductCreator}
                className={'w-full'}
                name={'launchDate'}
                disablePast
                defaultValue={formFields.launchDate}
                label={intl.formatMessage({ id: 'product.launchDate.label' })}
              />
            </div>

            <div className={'mt-4'}>
              <FormCalendar
                disabled={isProductCloseDateOutdated || !isUserHasAccessToProduct}
                disablePast
                className={'w-full'}
                name={'closeDate'}
                label={intl.formatMessage({ id: 'product.closeDate.label' })}
                minDate={formFields.launchDate || new Date()}
                rules={{
                  validate: (value: Date) => {
                    if (!value) return true;
                    const isBeforeToday = dayjs(value).isBefore(new Date(), 'day');
                    const isBeforeLaunchDate = dayjs(value).isBefore(formFields.launchDate, 'day');

                    if (isBeforeToday) {
                      return intl.formatMessage({
                        id: 'common.error.mustBeInFuture',
                      });
                    }

                    if (isBeforeLaunchDate) {
                      return intl.formatMessage({
                        id: 'common.error.notBeforeLaunch',
                      });
                    }
                    return !!value;
                  },
                }}
              />
            </div>
            <div className={'mt-4 flex items-center'}>
              <FormControlLabel
                control={
                  <FormCheckbox
                    name={'isBrokerOnly'}
                    disabled={!isUserProductCreator || isProductClosed || isProductActive}
                  />
                }
                label={<FormattedMessage id={'product.isBrokerOnly.label'} />}
              />
            </div>
            <div className={'mt-4 flex items-center'}>
              <FormControlLabel
                control={<FormCheckbox name={'isVerified'} disabled={isVerifiedCheckBlocked} />}
                label={<FormattedMessage id={'product.isVerified.label'} />}
              />
            </div>
            <div className={'mt-4 flex items-center'}>
              <FormControlLabel
                control={<FormCheckbox name={'isRestricted'} />}
                label={<FormattedMessage id={'product.isRestricted.label'} />}
              />
            </div>
          </div>
          <div className={'flex items-center gap-4'}>
            <LoadingButton
              isLoading={isLoading || isVerifyLoading}
              buttonProps={{ variant: 'contained', type: 'submit', disabled: !isDirtyFields }}
            >
              <FormattedMessage id={'cta.submit'} />
            </LoadingButton>
            <Link
              to={getPageURLWithSection(PageURL.PRODUCTS_NEW, undefined, {
                cloneFromProductId: productDetails.id,
              })}
            >
              <Button icon={<CloneIcon htmlColor={'currentColor'} fontSize={16} />}>Clone</Button>
            </Link>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
